import React from 'react';
import { Typography, Container, Grid, Box, Card, CardContent, Divider } from '@mui/material';
import { Warning, Security, Psychology } from '@mui/icons-material';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 pb-12">
      <Box className="py-16 bg-gradient-to-r from-purple-700 to-blue-500 text-white mb-8">
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" className="font-bold mb-4 text-center">
            About VIBE Coding
          </Typography>
          <Typography variant="h6" className="text-center">
            Understanding the evolution of AI-assisted development
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" component="h2" className="mb-4 font-bold text-gray-800">
              What is VIBE Coding?
            </Typography>
            <Typography variant="body1" className="mb-4 text-gray-700">
              VIBE Coding (Very Intelligent Bot-Enhanced Coding) represents a paradigm shift in software development. 
              It's an approach where developers leverage large language models (LLMs) to generate, modify, and 
              optimize code through natural language interactions.
            </Typography>
            <Typography variant="body1" className="mb-4 text-gray-700">
              Rather than writing every line of code manually, developers using VIBE Coding describe what they want 
              to build, and AI tools translate those requirements into functional code. The developer then reviews, 
              tests, and iteratively refines this code through continued dialogue with the AI.
            </Typography>
            <Typography variant="body1" className="mb-6 text-gray-700">
              This collaborative workflow between human creativity and AI capabilities allows for rapid prototyping, 
              exploration of multiple approaches, and a focus on higher-level concerns like architecture and user experience.
            </Typography>

            <Divider className="my-8" />

            <Typography variant="h4" component="h2" className="mb-4 font-bold text-gray-800">
              The VIBE Coding Workflow
            </Typography>
            <Typography variant="body1" className="mb-4 text-gray-700">
              1. <strong>Problem Description:</strong> Developers articulate what they want to build using natural language.
            </Typography>
            <Typography variant="body1" className="mb-4 text-gray-700">
              2. <strong>AI Code Generation:</strong> LLMs translate requirements into code that meets specifications.
            </Typography>
            <Typography variant="body1" className="mb-4 text-gray-700">
              3. <strong>Human Review:</strong> Developers review the generated code, looking for correctness, efficiency, and style.
            </Typography>
            <Typography variant="body1" className="mb-4 text-gray-700">
              4. <strong>Iterative Refinement:</strong> Through conversation with the AI, developers refine the code, 
              requesting changes, optimizations, or alternative approaches.
            </Typography>
            <Typography variant="body1" className="mb-6 text-gray-700">
              5. <strong>Testing & Integration:</strong> Developers test the code, fix issues, and integrate it into the larger project.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className="sticky top-6 shadow-lg mb-6">
              <CardContent>
                <Typography variant="h5" component="h3" className="font-bold mb-4 text-gray-800">
                  VIBE Coding vs. Traditional Development
                </Typography>
                <Box className="mb-4">
                  <Typography variant="subtitle1" className="font-bold text-gray-800">Traditional Development:</Typography>
                  <ul className="pl-5 list-disc text-gray-700">
                    <li>Manual writing of code line by line</li>
                    <li>Deep focus on syntax and implementation details</li>
                    <li>Slower prototyping and iteration cycles</li>
                    <li>Higher learning curve for non-experts</li>
                  </ul>
                </Box>
                <Box>
                  <Typography variant="subtitle1" className="font-bold text-gray-800">VIBE Coding:</Typography>
                  <ul className="pl-5 list-disc text-gray-700">
                    <li>Natural language descriptions translated to code</li>
                    <li>Focus on problem-solving and architecture</li>
                    <li>Rapid prototyping and exploration of alternatives</li>
                    <li>More accessible to domain experts without deep coding experience</li>
                  </ul>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider className="my-8" />

        {/* Risks and Challenges Section */}
        <Typography variant="h4" component="h2" className="mb-6 font-bold text-gray-800">
          Risks and Challenges of VIBE Coding
        </Typography>
        <Grid container spacing={4} className="mb-12">
          <Grid item xs={12} md={4}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <Warning className="text-amber-500 text-5xl mb-4" />
                <Typography variant="h5" component="h3" className="font-bold mb-2 text-gray-800">
                  Security Vulnerabilities
                </Typography>
                <Typography variant="body1" className="text-gray-700">
                  AI-generated code may contain security flaws or vulnerabilities if not properly reviewed. 
                  Thorough security testing and proper authentication systems like Rownd are essential to 
                  protect your applications and user data.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <Security className="text-amber-500 text-5xl mb-4" />
                <Typography variant="h5" component="h3" className="font-bold mb-2 text-gray-800">
                  Authentication Concerns
                </Typography>
                <Typography variant="body1" className="text-gray-700">
                  User management and authentication remain critical components that require careful implementation. 
                  VIBE Coding projects must still implement proper authentication systems to protect user data and 
                  prevent unauthorized access.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <Psychology className="text-amber-500 text-5xl mb-4" />
                <Typography variant="h5" component="h3" className="font-bold mb-2 text-gray-800">
                  Over-Reliance on AI
                </Typography>
                <Typography variant="body1" className="text-gray-700">
                  Developers may over-rely on AI solutions without understanding the underlying code, leading to maintenance 
                  challenges. A balanced approach is necessary, combining AI assistance with human expertise and review.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* How Rownd Helps Section */}
        <Box className="py-8 px-8 bg-gray-800 text-white rounded-lg mb-12">
          <Typography variant="h4" component="h2" className="mb-6 font-bold">
            How Rownd Addresses These Challenges
          </Typography>
          <Typography variant="body1" className="mb-4">
            Rownd provides a secure, pre-built authentication system that can be integrated into VIBE Coding projects 
            in minutes. By implementing Rownd, developers can:
          </Typography>
          <ul className="pl-5 list-disc mb-4">
            <li className="mb-2">
              <strong>Focus on Core Features:</strong> Let Rownd handle the complex authentication logic while you focus on your application's unique value.
            </li>
            <li className="mb-2">
              <strong>Ensure Security Compliance:</strong> Benefit from enterprise-grade security protocols without having to implement them yourself.
            </li>
            <li className="mb-2">
              <strong>Streamline User Experience:</strong> Offer passwordless sign-in options that reduce friction for your users.
            </li>
            <li className="mb-2">
              <strong>Monetize Effectively:</strong> Easily implement user management that supports subscription models and paid features.
            </li>
          </ul>
          <Typography variant="body1">
            By combining the speed and innovation of VIBE Coding with Rownd's secure authentication, you get the best of both worlds: 
            rapid development with enterprise-level security.
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default AboutPage; 